import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Download,
  Email,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { superAdminService } from '../../../services/superAdmin/superAdminService';
import { exportToExcel } from '../../../utils/excelExport';

const InvoiceListPage = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data, isLoading } = useQuery({
    queryKey: ['superAdminInvoices', paginationModel.page, paginationModel.pageSize],
    queryFn: async () => {
      const result = await superAdminService.getInvoices({
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize
      });
      if (!result.success) throw new Error(result.message);
      return result;
    }
  });

  const resendMutation = useMutation({
    mutationFn: (id) => superAdminService.resendInvoice(id),
    onSuccess: (result) => {
      setSnackbar({ open: true, message: result.message || 'Invoice email resent.', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['superAdminInvoices'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to resend invoice.', severity: 'error' });
    }
  });

  const handleDownload = async (id, invoiceNumber) => {
    try {
      const blob = await superAdminService.downloadInvoice(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || 'Invoice'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Failed to download invoice.', severity: 'error' });
    }
  };

  const handleExport = useCallback(() => {
    if (!data?.data) return;
    const totalAmount = data.data.reduce((sum, inv) => sum + (Number(inv.TotalAmount) || 0), 0);
    exportToExcel({
      title: 'Invoices',
      fileName: 'EasyCheck_Invoices.xlsx',
      sheetName: 'Invoices',
      columns: [
        { header: 'Invoice #', key: 'InvoiceNumber', type: 'text' },
        { header: 'Company', key: 'CompanyName', type: 'text', width: 25 },
        { header: 'Customer', key: 'CustomerName', type: 'text', width: 25 },
        { header: 'Email', key: 'CustomerEmail', type: 'text', width: 30 },
        { header: 'Plan', key: 'PlanName', type: 'text' },
        { header: 'Amount', key: 'TotalAmount', type: 'amount' },
        { header: 'Currency', key: 'Currency', type: 'text' },
        { header: 'Payment Method', key: 'PaymentMethod', type: 'text' },
        { header: 'Email Sent', key: 'EmailSent', type: 'text' },
        { header: 'Date', key: 'InvoiceDate', type: 'date' },
      ],
      data: data.data.map(inv => ({
        ...inv,
        CompanyName: inv.CompanyName || '',
        TotalAmount: Number(inv.TotalAmount || 0),
        EmailSent: inv.EmailSent ? 'Yes' : 'No',
        InvoiceDate: inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString('en-GB') : '',
      })),
      totals: { 'Amount': totalAmount },
    });
  }, [data]);

  const columns = [
    { field: 'InvoiceNumber', headerName: 'Invoice #', width: 140 },
    { field: 'CompanyName', headerName: 'Company', flex: 1, minWidth: 150 },
    { field: 'CustomerName', headerName: 'Customer', width: 150 },
    {
      field: 'TotalAmount',
      headerName: 'Amount',
      width: 120,
      renderCell: ({ row }) => (
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {row.Currency || 'AED'} {Number(row.TotalAmount || 0).toLocaleString()}
        </Typography>
      )
    },
    { field: 'PaymentMethod', headerName: 'Payment', width: 110 },
    {
      field: 'EmailSent',
      headerName: 'Email',
      width: 70,
      renderCell: ({ value }) => (
        value ? <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} /> : <Cancel sx={{ color: '#94a3b8', fontSize: 18 }} />
      )
    },
    {
      field: 'InvoiceDate',
      headerName: 'Date',
      width: 110,
      valueFormatter: (params) => {
        const val = params?.value ?? params;
        return val ? new Date(val).toLocaleDateString() : '';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Download PDF">
            <IconButton size="small" onClick={() => handleDownload(row.Id, row.InvoiceNumber)}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resend Email">
            <IconButton size="small" onClick={() => resendMutation.mutate(row.Id)} disabled={resendMutation.isPending}>
              <Email fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Invoices</Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          size="small"
          sx={{ borderColor: '#14b8a6', color: '#14b8a6', '&:hover': { borderColor: '#0d9488', backgroundColor: '#f0fdfa' } }}
        >
          Export Excel
        </Button>
      </Box>

      <Box sx={{ height: 'calc(100vh - 220px)', backgroundColor: 'white', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.Id}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc' }
          }}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceListPage;
